// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import SearchableSelect from '../common/SearchableSelect'
import { useEffect, useState } from 'react'
import { Layers, X } from 'lucide-react'
import { friendlyError } from '../../api/errors'
import { getUserGroups, setUserGroups, groupTypeLabel, type UserGroups } from '../../api/groups'

/**
 * D-RBAC-1 (G-1) — the account's groups on the user page: user groups it is in (removable), user
 * groups it can be added to, and the role / provider groups it reaches indirectly with
 * the reason ("holds a member role"). Same props as the other access sections.
 */
export default function UserGroupsSection({ userId, canWrite, onChanged }: { userId: string; roles: { id: string; name: string }[]; canWrite: boolean; onChanged: () => void }) {
  const [d, setD] = useState<UserGroups | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [pick, setPick] = useState('')
  const load = () => getUserGroups(userId).then(setD).catch(e => setErr(friendlyError(e)))
  useEffect(() => { void load() }, [userId])   // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async (ids: string[]) => {
    setBusy(true); setErr(null)
    try { await setUserGroups(userId, ids); await load(); onChanged() }
    catch (e) { setErr(friendlyError(e)) } finally { setBusy(false) }
  }
  if (!d && !err) return null
  const direct = d?.direct ?? []
  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--color-border)' }}>
      <h4 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}><Layers size={14} /> Groups</h4>
      <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 10 }}>What a group grants is added to this account on its next request.</div>
      {err && <div role="alert" style={{ fontSize: 12.5, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {direct.map(g => (
          <span key={g.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, padding: '3px 10px', borderRadius: 20, background: '#e0f2fe', color: '#075985', fontWeight: 600 }}>
            {g.name}
            {canWrite && <button type="button" aria-label={`Remove from ${g.name}`} disabled={busy} onClick={() => void apply(direct.filter(x => x.id !== g.id).map(x => x.id))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#075985', padding: 0, display: 'inline-flex' }}><X size={12} /></button>}
          </span>
        ))}
        {d?.via.map(v => (
          <span key={v.group.id} title={v.via} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, padding: '3px 10px', borderRadius: 20, background: '#f1f5f9', color: '#334155' }}>
            {v.group.name} <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>· {groupTypeLabel(v.group.group_type)} group, {v.via}</span>
          </span>
        ))}
        {direct.length === 0 && (d?.via.length ?? 0) === 0 && <span style={{ fontSize: 12.5, color: 'var(--color-text-mute)' }}>Not in any group.</span>}
      </div>
      {canWrite && (d?.available.length ?? 0) > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <SearchableSelect value={pick} onChange={setPick} ariaLabel="Add to group" placeholder="Add to a user group…"
            style={{ width: 260 }} options={d!.available.map(g => ({ value: g.id, label: g.name }))} />
          <button type="button" disabled={!pick || busy} onClick={() => { void apply([...direct.map(x => x.id), pick]); setPick('') }}
            style={{ fontSize: 12.5, padding: '6px 12px', borderRadius: 8, border: 'none', background: 'var(--color-primary, #185FA5)', color: '#fff', fontWeight: 600, cursor: pick ? 'pointer' : 'default', opacity: pick ? 1 : .55 }}>Add</button>
        </div>
      )}
    </div>
  )
}
